<template>  
  <div class="signer-list-field" :class="{ 'field-skipped': skipped, 'signer-list-field--error': !!errorMessage }">
    <div class="d-flex align-center justify-space-between mb-2">
      <div class="d-flex align-center ga-2">
        <v-icon
          v-if="!skipped" 
          size="14"
          color="grey-lighten-1"
          class="skip-btn"
          @click="emit('skip', true)"
        >mdi-close</v-icon>
        <span class="text-subtitle-2">{{ label }}</span>
        <a v-if="skipped" class="restore-link" @click="emit('skip', false)">Restore</a>
      </div>
      <div v-if="!skipped" class="d-flex ga-2">
        <v-btn
          v-if="signers.length > 0"
          variant="tonal"
          size="small"
          color="error"
          @click="clearAll"
        >
          <v-icon start size="16">mdi-delete-outline</v-icon>
          Clear All
        </v-btn>
        <v-btn
          v-if="canPickOfficers"
          variant="tonal"
          size="small"
          color="primary"
          @click="openDialog"
        >
          <v-icon start size="16">mdi-account-plus</v-icon>
          Add Signers
        </v-btn>
      </div>
    </div>

    <p v-if="!skipped && !canPickOfficers" class="text-caption text-medium-emphasis mb-2">
      Company is not incorporated yet — add or edit signers by typing a name below.
    </p>

    <template v-if="!skipped">
      <!-- Selected signers list -->
      <div v-if="displaySigners.length" class="signer-chips mb-2">
        <v-chip
          v-for="(signer, idx) in displaySigners"
          :key="signer.id"
          closable
          class="ma-1"
          :color="signer.isManual ? 'grey' : 'primary'"
          variant="outlined"
          @click:close="removeSigner(idx)"
        >
          <v-icon start size="16">{{ signer.isManual ? 'mdi-account-edit' : 'mdi-account' }}</v-icon>
          {{ signer.name }}
          <span v-if="signer.showTitle !== false && (signer.customTitle || signer.title)" class="text-caption ml-1">({{ signer.customTitle || signer.title }})</span>
        </v-chip>
      </div>

      <div v-else class="text-body-2 text-medium-emphasis pa-3 text-center" :style="{ border: errorMessage ? '1px dashed #ff5252' : '1px dashed #ccc', borderRadius: '8px' }">
        {{ canPickOfficers ? 'No signers selected. Click "Add Signers" to select.' : 'No signers yet. Enter a name below and press +.' }}
      </div>
      <div v-if="errorMessage" class="text-caption text-error mt-1">{{ errorMessage }}</div>
    </template>
    <div v-else class="text-body-2 text-medium-emphasis pa-3 text-center" style="opacity: 0.5;">
      Skipped
    </div>

    <!-- Manual entry -->
    <div class="d-flex align-center ga-2 mt-2">
      <v-text-field
        ref="manualNameInput"
        v-model="manualName"
        density="compact"
        variant="outlined"
        placeholder="Enter name manually..."
        hide-details
        @keyup.enter="addManualSigner"
      />
      <v-btn
        icon
        size="small"
        variant="tonal"
        color="primary"
        :disabled="!manualName.trim()"
        @click="addManualSigner"
      >
        <v-icon size="18">mdi-plus</v-icon>
      </v-btn>
    </div>

    <!-- ═══ Signer Selection Dialog ═══ -->
    <v-dialog v-model="dialogOpen" max-width="900" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center ga-2">
          <v-icon color="primary">mdi-pen</v-icon>
          Select Signers
        </v-card-title>
        <v-divider />

        <v-card-text class="pa-4">
          <!-- No entity -->
          <div v-if="!entityCd" class="text-center pa-6">
            <v-icon size="48" color="grey-lighten-1">mdi-domain-off</v-icon>
            <div class="text-body-1 text-medium-emphasis mt-2">Please select an entity first.</div>
          </div>

          <template v-else>
            <!-- Role tabs (각 탭에 인원 카운트 표시) -->
            <v-tabs v-model="selectedRoleIndex" color="primary" density="compact" class="mb-4">
              <v-tab v-for="role in roles" :key="role.key" :value="role.key">
                <v-icon start size="16">{{ role.icon }}</v-icon>
                {{ tabLabel(role.key) }}
              </v-tab>
            </v-tabs>

            <!-- Search box (이름 / 이메일 / 직책 / Reg No 검색) -->
            <v-text-field
              v-model="officerSearchText"
              :placeholder="`Search ${currentRoleLabel.toLowerCase()} by name, email, position...`"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="mb-2"
            />

            <!-- Loading -->
            <div v-if="loading" class="text-center pa-6">
              <v-progress-circular indeterminate size="32" color="primary" />
              <div class="text-caption mt-2">Loading...</div>
            </div>

            <!-- Officer list -->
            <template v-if="!loading && filteredRoleOfficers.length > 0">
              <!-- Select All -->
              <div class="d-flex align-center px-2 mb-1">
                <v-checkbox-btn
                  :model-value="allRoleSelected"
                  :indeterminate="someRoleSelected && !allRoleSelected"
                  @update:model-value="toggleSelectAll"
                  density="compact"
                  hide-details
                />
                <span class="text-body-2 text-medium-emphasis">
                  Select All ({{ filteredRoleOfficers.length }}<span
                    v-if="filteredRoleOfficers.length !== roleOfficers.length"
                  > of {{ roleOfficers.length }}</span>)
                </span>
              </div>

              <v-list density="compact" class="officer-list">
                <v-list-item
                  v-for="officer in filteredRoleOfficers"
                  :key="officer.id"
                  @click="toggleSigner(officer)"
                >
                  <template #prepend>
                    <v-checkbox-btn
                      :model-value="isSelected(officer.id)"
                      @update:model-value="toggleSigner(officer)"
                      density="compact"
                      hide-details
                    />
                  </template>
                  <v-list-item-title>{{ officer.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ officer.title }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </template>

            <div v-else-if="!loading && roleOfficers.length > 0 && filteredRoleOfficers.length === 0" class="text-center pa-6 text-medium-emphasis">
              No {{ currentRoleLabel.toLowerCase() }} matched "<strong>{{ officerSearchText }}</strong>".
              <div class="mt-2">
                <v-btn size="x-small" variant="text" color="primary" @click="officerSearchText = ''">
                  Clear search
                </v-btn>
              </div>
            </div>
            <div v-else-if="!loading" class="text-center pa-6 text-medium-emphasis">
              No {{ currentRoleLabel }} found for this entity.
            </div>

            <!-- ── Selected Signers with title options ── -->
            <div v-if="dialogSelected.length > 0" class="mt-4">
              <v-divider class="mb-3" />
              <div class="text-subtitle-2 mb-2">Selected Signers</div>

              <!-- ── Date format selector (공통) ── -->
              <div class="d-flex align-center ga-2 mb-3">
                <v-icon size="16" color="grey">mdi-calendar-clock</v-icon>
                <span class="text-caption text-medium-emphasis flex-shrink-0">Date format:</span>
                <v-select
                  v-model="detailDateFormat"
                  :items="dateFormatOptions"
                  item-title="title"
                  item-value="value"
                  density="compact"
                  variant="outlined"
                  hide-details
                  style="max-width: 300px;"
                />
                <v-chip
                  v-if="detailDateFormat && detailDateFormat === userDateFormat"
                  size="x-small"
                  color="primary"
                  variant="tonal"
                >Default</v-chip>
              </div>

              <!-- ── Individual mode ── -->
              <template v-if="dialogSelected.length > 0">
                <div v-for="(signer, idx) in dialogSelected" :key="'opt-' + signer.id" class="signer-title-row mb-2">
                  <div class="signer-name-header d-flex align-center ga-2 px-2 py-1 rounded">
                    <v-icon size="16" color="primary">mdi-account</v-icon>
                    <span class="text-body-2 font-weight-medium">{{ signer.name }}</span>
                    <v-chip size="x-small" variant="tonal">{{ signer.role }}</v-chip>
                    <v-spacer />
                    <v-btn
                      v-if="dialogSelected.length > 1"
                      size="x-small"
                      variant="tonal"
                      color="success"
                      @click="applySignerPatternToAll(idx)"
                    >
                      <v-icon start size="12">mdi-check-all</v-icon>
                      Apply to All
                    </v-btn>
                  </div>
                  <div class="d-flex align-center ga-2 mt-1 pl-6">
                    <v-checkbox
                      :model-value="signer.showTitle !== false"
                      @update:model-value="updateSignerField(idx, 'showTitle', $event ?? false)"
                      label="Show title"
                      density="compact"
                      hide-details
                      class="flex-grow-0"
                    />
                    <v-text-field
                      :model-value="signer.customTitle || ''"
                      @update:model-value="updateSignerField(idx, 'customTitle', $event)"
                      :placeholder="signer.title || 'Enter title...'"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :disabled="signer.showTitle === false"
                      style="max-width: 240px;"
                    />
                  </div>
                  <div class="pl-6 mt-1">
                    <div
                      v-for="(line, lineIdx) in getSignerExtraLines(signer)"
                      :key="'line-' + idx + '-' + lineIdx"
                      class="d-flex align-center ga-1 mb-1"
                    >
                      <v-text-field
                        :model-value="line"
                        @update:model-value="updateExtraLine(idx, lineIdx, $event)"
                        :placeholder="lineIdx === 0 ? 'e.g. IC No.' : 'Additional line...'"
                        density="compact"
                        variant="outlined"
                        hide-details
                      />
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="removeExtraLine(idx, lineIdx)"
                      >
                        <v-icon size="16">mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <div class="d-flex align-center ga-2 mt-1">
                      <v-btn
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click="addExtraLine(idx)"
                      >
                        <v-icon start size="14">mdi-plus</v-icon>
                        Add line
                      </v-btn>

                      <v-menu :close-on-content-click="false">
                        <template #activator="{ props: menuProps }">
                          <v-btn v-bind="menuProps" size="x-small" variant="text" color="primary">
                            <v-icon start size="14">mdi-calendar-plus</v-icon>
                            Add date
                          </v-btn>
                        </template>
                        <v-card class="pa-3" min-width="300">
                          <v-text-field
                            v-model="datePickerLabel"
                            placeholder="e.g. Date of Resolution"
                            label="Label (optional)"
                            density="compact"
                            variant="outlined"
                            hide-details
                            class="mb-2"
                          />
                          <v-text-field
                            v-model="datePickerValue"
                            type="date"
                            label="Select date"
                            density="compact"
                            variant="outlined"
                            hide-details
                          />
                          <div v-if="datePickerValue" class="text-caption mt-2 text-medium-emphasis">
                            Preview: <strong>{{ datePickerPreview() }}</strong>
                          </div>
                          <v-btn
                            size="small"
                            variant="tonal"
                            color="primary"
                            class="mt-2"
                            block
                            :disabled="!datePickerValue"
                            @click="addDateToSigner(idx)"
                          >
                            <v-icon start size="14">mdi-check</v-icon>
                            Add
                          </v-btn>
                        </v-card>
                      </v-menu>
                    </div>
                  </div>

                  <!-- ── Detail fetch ── -->
                  <div v-if="canFetchDetail(signer)" class="pl-6 mt-1">
                    <v-btn
                      v-if="!signerDetailCache[signer.id]"
                      size="x-small"
                      variant="tonal"
                      color="secondary"
                      :loading="!!signerDetailLoading[signer.id]"
                      @click="fetchSignerDetail(signer)"
                    >
                      <v-icon start size="14">mdi-card-account-details-outline</v-icon>
                      Fetch details
                    </v-btn>

                    <div v-if="signerDetailCache[signer.id]" class="detail-fields mt-1">
                      <span class="text-caption text-medium-emphasis mr-1">Click to add:</span>
                      <v-chip
                        v-for="field in signerDetailCache[signer.id]"
                        :key="field.key"
                        size="small"
                        variant="outlined"
                        class="ma-1"
                        @click="addDetailToExtraLine(idx, field)"
                      >
                        <v-icon start size="14">mdi-plus-circle-outline</v-icon>
                        {{ field.label }}: {{ getDetailFieldDisplay(field) }}
                      </v-chip>
                      <span v-if="signerDetailCache[signer.id].length === 0" class="text-caption text-medium-emphasis">
                        No detail fields available.
                      </span>
                    </div>
                  </div>

                </div>
              </template>

              <!-- ── Signature Block Preview ── -->
              <v-divider class="my-3" />
              <div class="d-flex align-center ga-2 mb-2">
                <v-icon size="16" color="grey">mdi-eye-outline</v-icon>
                <span class="text-subtitle-2">Preview</span>
              </div>
              <div class="signature-preview">
                <table
                  v-for="(row, rowIdx) in previewRows"
                  :key="'row-' + rowIdx"
                  class="signature-preview-table"
                >
                  <tbody>
                    <tr>
                      <td v-for="signer in row" :key="'sig-' + signer.id" class="signature-cell">
                        <div class="signature-line-space"></div>
                        <div class="signature-line">____________________</div>
                      </td>
                    </tr>
                    <tr>
                      <td v-for="signer in row" :key="'name-' + signer.id" class="signature-cell">
                        <div class="signer-display-name">{{ getDisplayName(signer) }}</div>
                        <div
                          v-for="(line, li) in getExtraLines(signer)"
                          :key="li"
                          class="signer-extra-line"
                        >{{ line }}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <span class="text-body-2 text-medium-emphasis">
            Selected: {{ dialogSelected.length }}
          </span>
          <v-spacer />
          <v-btn variant="text" @click="dialogOpen = false">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="dialogSelected.length === 0"
            @click="confirmSelection"
          >
            <v-icon start size="16">mdi-check</v-icon>
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import type { SignerEntry } from '../types/docGen.types';
import { docGenApi } from '../api/docGenApi';
import { useDocGenStore } from '../stores/docGenStore';
import { useSessionStore } from '@/shared/stores/sessionStore';

interface NormalizedOfficer {
  id: string;
  name: string;
  title: string;
  role: string;
  email?: string;
  officerIdx?: number;
  contactCd?: string;
}

interface DetailField {
  key: string;
  label: string;
  value: string;        // 원본 API 값
  parsedDate?: Date;    // 날짜 필드인 경우 파싱된 Date 객체
}

// ── Date format (ChatDatePicker와 동일) ──────────────────────
const DATE_FORMAT_PATTERNS = [
  'YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY/MM/DD',
  'DD-MM-YYYY', 'YYYY.MM.DD', 'MM.DD.YYYY', 'DD.MM.YYYY',
  'dd MMM yyyy', 'd MMMM yyyy',
];

const MONTH_ABBR = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const DATE_FIELD_KEYS = new Set(['dateOfBirth', 'appointmentDate', 'cessationDate', 'dateOfIncorporation']);

function formatWithPattern(date: Date, pattern: string): string {
  let result = pattern;
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  result = result.replace(/YYYY/g, String(y));
  result = result.replace(/yyyy/g, String(y));
  result = result.replace(/MMMM/g, MONTH_FULL[m]);
  result = result.replace(/MMM/g, MONTH_ABBR[m]);
  result = result.replace(/MM/g, String(m + 1).padStart(2, '0'));
  result = result.replace(/DD/g, String(d).padStart(2, '0'));
  result = result.replace(/dd/g, String(d).padStart(2, '0'));
  result = result.replace(/\bD\b/g, String(d));
  result = result.replace(/\bd\b/g, String(d));
  return result;
}

/** 사용자 날짜포맷 기반으로 날짜 문자열 → Date 객체 파싱 */
function parseDateFromFormat(dateStr: string, pattern: string): Date | null {
  if (!dateStr || !pattern) return tryParseDateFallback(dateStr);

  // 텍스트 월 패턴 (dd MMM yyyy, d MMMM yyyy)
  if (/MMM/i.test(pattern)) {
    const parts = dateStr.split(/\s+/);
    if (parts.length >= 3) {
      const d = parseInt(parts[0]);
      const monthStr = parts[1].replace('.', '');
      const y = parseInt(parts[parts.length - 1]);
      let mi = MONTH_ABBR.findIndex((a) => a.replace('.', '').toLowerCase() === monthStr.toLowerCase());
      if (mi === -1) mi = MONTH_FULL.findIndex((f) => f.toLowerCase() === monthStr.toLowerCase());
      if (!isNaN(d) && !isNaN(y) && mi >= 0) return new Date(y, mi, d);
    }
    return tryParseDateFallback(dateStr);
  }

  // 숫자 패턴 — 구분자로 분리
  const sep = pattern.replace(/[A-Za-z]/g, '')[0] || '/';
  const fmtParts = pattern.split(sep);
  const valParts = dateStr.split(sep);
  if (fmtParts.length !== 3 || valParts.length !== 3) return tryParseDateFallback(dateStr);

  let day = 0, month = 0, year = 0;
  fmtParts.forEach((fp, i) => {
    const val = parseInt(valParts[i]);
    const u = fp.toUpperCase();
    if (u.startsWith('D')) day = val;
    else if (u.startsWith('M')) month = val;
    else if (u.startsWith('Y')) year = val;
  });

  if (day && month && year) return new Date(year, month - 1, day);
  return tryParseDateFallback(dateStr);
}

/** Fallback: 공통 날짜 형식 파싱 */
function tryParseDateFallback(dateStr: string): Date | null {
  if (!dateStr) return null;
  // ISO: YYYY-MM-DD
  const iso = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
  // DD/MM/YYYY (기본 fallback)
  const dmy = dateStr.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
  return null;
}

const props = withDefaults(defineProps<{
  modelValue: string | SignerEntry[];
  label: string;
  varKey: string;
  skipped?: boolean;
  errorMessage?: string;
  /**
   * 외부 entityCd 주입.
   * 빈 문자열을 넘기면 store entityKey 를 쓰지 않음(수동 서명만) —
   * Incorporation Pre 등 L_ENTITY 가 아직 없는 경우.
   */
  entityCdOverride?: string;
  /**
   * true 이면 officer API(Add Signers 모달) 비활성 — 수동 입력만.
   * SG/MY Incorporation (Pre) Task Detail Edit 등.
   */
  manualOnly?: boolean;
  /** 칩·목록 표시만 필터 (modelValue 저장값은 그대로) — Attendance: members 중복 숨김 */
  displaySignersFilter?: (signers: SignerEntry[]) => SignerEntry[];
}>(), {
  skipped: false,
  errorMessage: '',
  entityCdOverride: undefined,
  manualOnly: false,
  displaySignersFilter: undefined,
});

const emit = defineEmits<{
  update: [value: string];
  skip: [skipped: boolean];
}>();

const store = useDocGenStore();
const sessionStore = useSessionStore();
const entityCd = computed(() => {
  if (props.manualOnly) return '';
  // undefined 만 store fallback — 명시적 '' 는 officer 조회 차단
  if (props.entityCdOverride !== undefined) return String(props.entityCdOverride || '').trim();
  return String(store.entities[store.activeEntityIndex]?.entityKey || '').trim();
});
const canPickOfficers = computed(() => !props.manualOnly && !!entityCd.value);
const isJurisdictionMY = computed(() => {
  const j = sessionStore.user?.customer?.jurisdiction;
  return j === 'MY' || j === 'my';
});

// ── 날짜 포맷 (세션 기본값) ──
const userDateFormat = computed(() => sessionStore.user?.user?.dateFormat || 'DD/MM/YYYY');
const detailDateFormat = ref<string | null>(null);

/** 현재 선택된 날짜포맷 (미선택 시 세션 기본값) */
const effectiveDateFormat = computed(() => detailDateFormat.value ?? userDateFormat.value);

/** 날짜포맷 드롭다운 옵션 (오늘 날짜 기준 미리보기) */
const dateFormatOptions = computed(() => {
  const today = new Date();
  const defFmt = userDateFormat.value;
  return DATE_FORMAT_PATTERNS.map((p) => ({
    title: `${formatWithPattern(today, p)}  (${p})${p === defFmt ? ' ★' : ''}`,
    value: p,
  }));
});

/** DetailField의 표시 값 (날짜 필드는 선택된 포맷 적용) */
function getDetailFieldDisplay(field: DetailField): string {
  if (field.parsedDate) {
    return formatWithPattern(field.parsedDate, effectiveDateFormat.value);
  }
  return field.value;
}

const manualName = ref('');
const manualNameInput = ref<{ focus?: () => void } | null>(null);
const dialogOpen = ref(false);

// ── Parsed signers from modelValue (저장·지정용 전체) ──
const signers = computed<SignerEntry[]>(() => {
  if (!props.modelValue) return [];
  if (Array.isArray(props.modelValue)) return props.modelValue;
  if (typeof props.modelValue === 'string') {
    try {
      const parsed = JSON.parse(props.modelValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
});

/** UI 칩 표시용 — displaySignersFilter 가 있으면 표시만 축소 */
const displaySigners = computed<SignerEntry[]>(() => {
  const list = signers.value;
  if (!props.displaySignersFilter) return list;
  return props.displaySignersFilter(list);
});

// ── Dialog state ──
// 각 역할별 카운트 (탭 라벨에 표시) — dialog 진입 시 병렬 fetch
const roleCounts = ref<Record<string, number>>({});
const roleCountsLoading = ref(false);

const roles = computed(() => {
  const baseRoles: Array<{ key: string; label: string; icon: string }> = [
    { key: 'DIRECTOR', label: 'Director', icon: 'mdi-account-tie' },
    { key: 'SECRETARY', label: 'Secretary', icon: 'mdi-account-edit' },
  ];
  // Manager 탭 — MY 전용
  if (isJurisdictionMY.value) {
    baseRoles.push({ key: 'MANAGER', label: 'Manager', icon: 'mdi-account-supervisor' });
  }
  // SHAREHOLDER → MY 에선 "Member"
  baseRoles.push({
    key: 'SHAREHOLDER',
    label: isJurisdictionMY.value ? 'Member' : 'Shareholder',
    icon: 'mdi-account-multiple',
  });
  baseRoles.push({
    key: 'CONTROLLER',
    label: isJurisdictionMY.value ? 'Beneficial Owner' : 'Controller',
    icon: 'mdi-account-cog',
  });
  baseRoles.push({ key: 'CONTACT', label: 'Contact', icon: 'mdi-account-box' });
  return baseRoles;
});

function tabLabel(roleKey: string): string {
  const role = roles.value.find((r) => r.key === roleKey);
  if (!role) return roleKey;
  const count = roleCounts.value[roleKey];
  return count != null ? `${role.label} (${count})` : role.label;
}

const selectedRoleIndex = ref('DIRECTOR');
const loading = ref(false);
const roleOfficers = ref<NormalizedOfficer[]>([]);
const dialogSelected = ref<SignerEntry[]>([]);

/** 모든 역할별 카운트를 병렬로 fetch — dialog 진입 시 호출 */
async function fetchAllRoleCounts() {
  if (!entityCd.value) return;
  roleCountsLoading.value = true;
  const counts: Record<string, number> = {};
  const tasks: Array<Promise<unknown>> = [
    docGenApi.getDirectors(entityCd.value).then((r: any) => {
      counts.DIRECTOR = (r?.data?.directors || []).length;
    }).catch(() => { counts.DIRECTOR = 0; }),
    docGenApi.getSecretaries(entityCd.value).then((r: any) => {
      counts.SECRETARY = (r?.data?.secretaries || []).length;
    }).catch(() => { counts.SECRETARY = 0; }),
    docGenApi.getMembers(entityCd.value).then((r: any) => {
      counts.SHAREHOLDER = (r?.data?.members || r?.data?.shares || []).length;
    }).catch(() => { counts.SHAREHOLDER = 0; }),
    docGenApi.getBeneficialOwners(entityCd.value).then((r: any) => {
      // MY: data.beneficialOwners / SG: data.controllers / 또는 data 가 array
      const list = r?.data?.beneficialOwners || r?.data?.controllers
        || (Array.isArray(r?.data) ? r.data : []);
      counts.CONTROLLER = list.length;
    }).catch(() => { counts.CONTROLLER = 0; }),
    docGenApi.getContacts(entityCd.value).then((r: any) => {
      counts.CONTACT = (r?.data?.contacts || []).length;
    }).catch(() => { counts.CONTACT = 0; }),
  ];
  // Manager — MY 전용
  if (isJurisdictionMY.value) {
    tasks.push(
      docGenApi.getManagers(entityCd.value).then((r: any) => {
        counts.MANAGER = (r?.data?.managers || []).length;
      }).catch(() => { counts.MANAGER = 0; })
    );
  }
  await Promise.allSettled(tasks);
  roleCounts.value = counts;
  roleCountsLoading.value = false;
}

const currentRoleLabel = computed(() => {
  const role = roles.value.find((r) => r.key === selectedRoleIndex.value);
  return role?.label || 'Officer';
});

function openDialog() {
  // Incorporation Pre 등 — L_ENTITY 없음. officer API(AUTH002) 호출 금지, 수동 입력만.
  if (!canPickOfficers.value) {
    nextTick(() => {
      const el = manualNameInput.value as any;
      el?.focus?.() || el?.$el?.querySelector?.('input')?.focus?.();
    });
    return;
  }
  // 기존 선택 복사 (추가 선택 가능)
  dialogSelected.value = [...signers.value];
  // extraLinesMap 초기화
  extraLinesMap.value = {};
  for (const s of dialogSelected.value) {
    extraLinesMap.value[s.id] = s.extraText ? s.extraText.split('\n') : [];
  }
  selectedRoleIndex.value = 'DIRECTOR';
  batchDetailFetched.value = false;
  detailDateFormat.value = userDateFormat.value;
  signerDetailCache.value = {};
  officerSearchText.value = ''; // dialog 진입 시 검색어 초기화
  dialogOpen.value = true;
  loadOfficers();
  // 모든 역할의 카운트를 병렬로 조회 — 탭 라벨에 (N) 표시
  fetchAllRoleCounts();
}

function isSelected(id: string): boolean {
  return dialogSelected.value.some((s) => s.id === id);
}

// 검색어 (현재 탭의 officer 목록 필터링)
const officerSearchText = ref('');

// 검색어 적용한 filtered list — 이름 / 직책 / 이메일 / Reg No 부분 일치 (case-insensitive)
const filteredRoleOfficers = computed(() => {
  const q = String(officerSearchText.value || '').trim().toLowerCase();
  if (!q) return roleOfficers.value;
  return roleOfficers.value.filter((o: any) => {
    const name = String(o.name || '').toLowerCase();
    const title = String(o.title || '').toLowerCase();
    const email = String(o.email || '').toLowerCase();
    const regNo = String(o.contactRegNo || '').toLowerCase();
    return name.includes(q) || title.includes(q) || email.includes(q) || regNo.includes(q);
  });
});

// Select-All 은 filtered 기준 (검색 결과만 선택/해제)
const allRoleSelected = computed(() =>
  filteredRoleOfficers.value.length > 0 && filteredRoleOfficers.value.every((o) => isSelected(o.id)),
);
const someRoleSelected = computed(() =>
  filteredRoleOfficers.value.some((o) => isSelected(o.id)),
);

function toggleSelectAll() {
  if (allRoleSelected.value) {
    // 현재 검색 결과의 officer 만 제거
    const idsToRemove = new Set(filteredRoleOfficers.value.map((o) => o.id));
    dialogSelected.value = dialogSelected.value.filter((s) => !idsToRemove.has(s.id));
  } else {
    // 아직 선택되지 않은 검색 결과 officer 만 추가
    const newSigners: { signer: SignerEntry; idx: number }[] = [];
    for (const officer of filteredRoleOfficers.value) {
      if (!isSelected(officer.id)) {
        const signer: SignerEntry = {
          id: officer.id,
          name: officer.name,
          title: officer.title,
          role: officer.role,
          email: officer.email,
          showTitle: false,
          customTitle: '',
          officerIdx: officer.officerIdx,
          contactCd: officer.contactCd,
        };
        dialogSelected.value.push(signer);
        newSigners.push({ signer, idx: dialogSelected.value.length - 1 });
      }
    }
    // 배치 포맷이 있으면 새 사이너에 자동 적용
    if (lastAppliedBatchFormat.value || appliedDetailFieldKeys.value.length > 0) {
      for (const { signer, idx } of newSigners) {
        autoApplyBatchToSigner(signer, idx);
      }
    }
  }
}

function toggleSigner(officer: NormalizedOfficer) {
  const idx = dialogSelected.value.findIndex((s) => s.id === officer.id);
  if (idx >= 0) {
    dialogSelected.value.splice(idx, 1);
  } else {
    const signer: SignerEntry = {
      id: officer.id,
      name: officer.name,
      title: officer.title,
      role: officer.role,
      email: officer.email,
      showTitle: false,
      customTitle: '',
      officerIdx: officer.officerIdx,
      contactCd: officer.contactCd,
    };
    dialogSelected.value.push(signer);
    // 배치 포맷이 있으면 새 사이너에 자동 적용
    if (lastAppliedBatchFormat.value || appliedDetailFieldKeys.value.length > 0) {
      autoApplyBatchToSigner(signer, dialogSelected.value.length - 1);
    }
  }
}

function updateSignerField(index: number, field: 'showTitle' | 'customTitle' | 'extraText', value: boolean | string) {
  dialogSelected.value[index] = {
    ...dialogSelected.value[index],
    [field]: value,
  };
}

// ── Extra lines helpers ──
// extraLines는 각 signer별 줄 배열을 reactive로 관리 (extraText ↔ 동기화)
const extraLinesMap = ref<Record<string, string[]>>({});

function getSignerExtraLines(signer: SignerEntry): string[] {
  if (!extraLinesMap.value[signer.id]) {
    extraLinesMap.value[signer.id] = signer.extraText
      ? signer.extraText.split('\n')
      : [];
  }
  return extraLinesMap.value[signer.id];
}

function syncExtraText(signerIdx: number) {
  const signer = dialogSelected.value[signerIdx];
  const lines = extraLinesMap.value[signer.id] || [];
  dialogSelected.value[signerIdx] = {
    ...signer,
    extraText: lines.filter((l) => l.trim()).join('\n'),
  };
}

function updateExtraLine(signerIdx: number, lineIdx: number, value: string) {
  const signer = dialogSelected.value[signerIdx];
  const lines = getSignerExtraLines(signer);
  lines[lineIdx] = value;
  syncExtraText(signerIdx);
}

function removeExtraLine(signerIdx: number, lineIdx: number) {
  const signer = dialogSelected.value[signerIdx];
  const lines = getSignerExtraLines(signer);
  lines.splice(lineIdx, 1);
  syncExtraText(signerIdx);
}

function addExtraLine(signerIdx: number) {
  const signer = dialogSelected.value[signerIdx];
  const lines = getSignerExtraLines(signer);
  lines.push('');
}

// ── Batch apply ──
const applyMode = ref<'individual' | 'batch'>('individual');
const batchShowTitle = ref(true);
const batchCustomTitle = ref('');
const batchExtraLines = ref<string[]>(['']);

// 마지막 적용된 배치 포맷 (새 사이너 추가 시 자동 적용)
const lastAppliedBatchFormat = ref<{
  showTitle: boolean;
  customTitle: string;
  extraLines: string[];
} | null>(null);

// 적용된 디테일 필드 키 추적 (새 사이너 추가 시 자동 적용)
const appliedDetailFieldKeys = ref<{ key: string; label: string }[]>([]);

function applyBatch() {
  const filteredLines = batchExtraLines.value.filter((l) => l.trim());
  const extraText = filteredLines.join('\n');
  dialogSelected.value = dialogSelected.value.map((s) => {
    extraLinesMap.value[s.id] = [...filteredLines];
    return {
      ...s,
      showTitle: batchShowTitle.value,
      customTitle: batchCustomTitle.value,
      extraText,
    };
  });

  // 배치 포맷 저장 (새 사이너 자동 적용용)
  lastAppliedBatchFormat.value = {
    showTitle: batchShowTitle.value,
    customTitle: batchCustomTitle.value,
    extraLines: [...filteredLines],
  };
}

/** Individual 모드: 한 사이너의 패턴(title, extra lines)을 모든 사이너에 적용 */
async function applySignerPatternToAll(sourceIdx: number) {
  const source = dialogSelected.value[sourceIdx];
  if (!source) return;

  const sourceLines = getSignerExtraLines(source);

  // 소스 사이너의 디테일 캐시에서 실제 디테일 필드 라벨 목록 추출
  const sourceDetails = signerDetailCache.value[source.id];
  const knownDetailLabels = new Set(
    sourceDetails ? sourceDetails.map((f) => f.label) : []
  );

  for (let i = 0; i < dialogSelected.value.length; i++) {
    if (i === sourceIdx) continue;
    const target = dialogSelected.value[i];

    // showTitle, customTitle 복사
    dialogSelected.value[i] = {
      ...target,
      showTitle: source.showTitle,
      customTitle: source.customTitle,
    };

    // 타겟 디테일이 없으면 먼저 fetch
    if (knownDetailLabels.size > 0 && canFetchDetail(target) && !signerDetailCache.value[target.id]) {
      await fetchSignerDetail(target);
    }
    const targetDetails = signerDetailCache.value[target.id];

    // extra lines: 디테일 필드만 타겟 값으로 치환, 나머지는 그대로 복사
    const newLines: string[] = [];
    for (const line of sourceLines) {
      const colonIdx = line.indexOf(':');
      const labelPart = colonIdx >= 0 ? line.slice(0, colonIdx).trim() : null;

      if (labelPart && knownDetailLabels.has(labelPart) && targetDetails) {
        // 실제 디테일 필드 → 타겟 사이너 값으로 치환
        const match = targetDetails.find((f) => f.label === labelPart);
        if (match) {
          newLines.push(`${labelPart}: ${getDetailFieldDisplay(match)}`);
          continue;
        }
      }
      // 일반 라인 (Date, 직접 입력 등) → 그대로 복사
      newLines.push(line);
    }
    extraLinesMap.value[target.id] = newLines;
    dialogSelected.value[i].extraText = newLines.join('\n');
  }

  // 배치 포맷도 저장 (새 사이너 추가 시 자동 적용)
  lastAppliedBatchFormat.value = {
    showTitle: source.showTitle !== false,
    customTitle: source.customTitle || '',
    extraLines: [...sourceLines],
  };

  // 적용된 디테일 필드 키 저장
  if (knownDetailLabels.size > 0) {
    appliedDetailFieldKeys.value = [...knownDetailLabels].map((label) => ({ key: label, label }));
  }
}

/** 새로 추가된 사이너에 기존 배치 포맷 + 디테일 필드 자동 적용 */
async function autoApplyBatchToSigner(signer: SignerEntry, signerIdx: number) {
  // 1. 배치 포맷 적용 (showTitle, customTitle, extraLines)
  if (lastAppliedBatchFormat.value) {
    const fmt = lastAppliedBatchFormat.value;
    extraLinesMap.value[signer.id] = [...fmt.extraLines];
    dialogSelected.value[signerIdx] = {
      ...dialogSelected.value[signerIdx],
      showTitle: fmt.showTitle,
      customTitle: fmt.customTitle,
      extraText: fmt.extraLines.join('\n'),
    };
  }

  // 2. 디테일 필드가 적용되어 있으면 새 사이너 상세 조회 후 적용
  if (appliedDetailFieldKeys.value.length > 0 && canFetchDetail(signer)) {
    if (!signerDetailCache.value[signer.id]) {
      await fetchSignerDetail(signer);
    }
    const updatedIdx = dialogSelected.value.findIndex((s) => s.id === signer.id);
    if (updatedIdx >= 0) {
      for (const { key, label } of appliedDetailFieldKeys.value) {
        const fields = signerDetailCache.value[signer.id];
        const field = fields?.find((f) => f.key === key);
        if (field) {
          const lines = getSignerExtraLines(dialogSelected.value[updatedIdx]);
          const displayVal = getDetailFieldDisplay(field);
          const lineText = `${label}: ${displayVal}`;
          if (!lines.includes(lineText)) {
            lines.push(lineText);
            syncExtraText(updatedIdx);
          }
        }
      }
    }
  }
}

// ── Signer detail fetch ──
const signerDetailCache = ref<Record<string, DetailField[]>>({});
const signerDetailLoading = ref<Record<string, boolean>>({});

const DETAIL_FIELD_MAP: [string, string][] = [
  ['contactRegNo', 'Registration No'],
  ['idTypeName', 'ID Type'],
  ['nationalityName', 'Nationality'],
  ['dateOfBirth', 'Date of Birth'],
  ['primaryAddress', 'Address'],
  ['contactEmail', 'Email'],
  ['mobileNo', 'Mobile'],
];

async function fetchSignerDetail(signer: SignerEntry) {
  if (signerDetailCache.value[signer.id]) return;
  if (!entityCd.value) return;

  signerDetailLoading.value = { ...signerDetailLoading.value, [signer.id]: true };
  try {
    let detail: any = null;
    const role = signer.role || '';
    const oidx = signer.officerIdx;
    const ccd = signer.contactCd || '';

    // ⭐ MY BO: list 응답에 이미 detail 이 다 있어 loadOfficers 에서 cache 채움.
    //    여기 도달했다는 건 cache 가 비어있다 = 다른 경로로 추가됐을 수 있으므로
    //    BO endpoint 에서 다시 list 받아 본인 row 찾아 매핑.
    if (role === 'controller' && isJurisdictionMY.value) {
      try {
        const boRes = await docGenApi.getBeneficialOwners(entityCd.value);
        const list = boRes?.data?.beneficialOwners || [];
        const matched = list.find((c: any) =>
          String(c.id ?? c.ctrlIdx ?? c.contactCd ?? '') === String(signer.id) ||
          (ccd && c.contactCd === ccd) ||
          (signer.name && (c.name === signer.name || c.contactName === signer.name))
        );
        if (matched) detail = matched;
      } catch { /* fall back to legacy paths below */ }
    }

    // BO 가 매칭됐으면 fallback 체인 통째로 skip
    if (!detail) {
      if ((role === 'director' || role === 'secretary' || role === 'manager') && oidx != null) {
        // officer 상세 조회 (officerIdx 기반) — Director/Secretary/Manager 는 모두 L_OFFICER
        const res = await docGenApi.getOfficerDetail(entityCd.value, oidx);
        detail = res?.data;
      } else if (ccd) {
        // contact 상세 조회 (contactCd 기반)
        // MY Beneficial Owner(Controller)는 contact profile이 entity_my/controller 아래에 있음 (legacy)
        if (role === 'controller' && isJurisdictionMY.value) {
          const { apiClient } = await import('@/shared/api/client');
          const res = await apiClient.get(`/entity_my/controller/contact-profile/${ccd}`) as any;
          detail = res?.data?.data || res?.data;
        } else {
          const res = await docGenApi.getContactDetail(entityCd.value, ccd);
          detail = res?.data;
        }
      } else if (signer.id) {
      // fallback: id를 officerIdx 또는 contactCd로 추정
      if (role === 'director' || role === 'secretary') {
        const numId = Number(signer.id);
        if (!isNaN(numId)) {
          const res = await docGenApi.getOfficerDetail(entityCd.value, numId);
          detail = res?.data;
        }
      } else {
        // controller, shareholder, contact 등 — id를 contactCd로 시도
        if (role === 'controller' && isJurisdictionMY.value) {
          const { apiClient } = await import('@/shared/api/client');
          // ⚠️ MY controller list에서 id는 보통 ctrlIdx(숫자)라서 contact-profile에 직접 넣으면 500이 납니다.
          // 1) controller detail로 contactCd 확보 → 2) contact-profile 호출
          const ctrlIdx = String(signer.id || '').trim();
          const detailRes = await apiClient.get(`/entity_my/controller/${entityCd.value}/detail`, {
            params: { ctrlIdx }
          }) as any;
          const ctrlDetail = detailRes?.data?.data || detailRes?.data;
          const contactCd = String(ctrlDetail?.contactCd || ctrlDetail?.referencesContactCd || '').trim();
          if (contactCd) {
            const profileRes = await apiClient.get(`/entity_my/controller/contact-profile/${contactCd}`) as any;
            const profile = profileRes?.data?.data || profileRes?.data;

            // Normalize into keys used by DETAIL_FIELD_MAP
            const addressParts = [
              ctrlDetail?.addressLine1,
              ctrlDetail?.addressLine2,
              ctrlDetail?.addressLine3,
              ctrlDetail?.postcode || ctrlDetail?.post_code,
              ctrlDetail?.city,
              ctrlDetail?.state,
              ctrlDetail?.country,
            ]
              .map((v: any) => String(v || '').trim())
              .filter(Boolean);

            const addrFromProfile =
              (profile?.residentialAddress ? JSON.stringify(profile.residentialAddress) : '') ||
              (profile?.serviceAddress ? JSON.stringify(profile.serviceAddress) : '') ||
              (profile?.businessAddress ? JSON.stringify(profile.businessAddress) : '');

            detail = {
              ...ctrlDetail,
              // standard keys for chips
              // MY controller detail uses identificationNo / acrAuen / nonAcrAuen (not contactRegNo)
              contactRegNo:
                ctrlDetail?.contactRegNo ||
                ctrlDetail?.refContactRegNo ||
                ctrlDetail?.registrationNo ||
                ctrlDetail?.identificationNo ||
                ctrlDetail?.acrAuen ||
                ctrlDetail?.nonAcrAuen ||
                '',
              idTypeName: ctrlDetail?.idTypeName || ctrlDetail?.refIdTypeName || '',
              nationalityName: ctrlDetail?.nationalityName || ctrlDetail?.refNationalityName || '',
              dateOfBirth: ctrlDetail?.dateOfBirth || '',
              contactEmail: ctrlDetail?.contactEmail || '',
              mobileNo: ctrlDetail?.mobileNo || '',
              primaryAddress: addressParts.join(', ') || addrFromProfile || '',
            };
          } else {
            // contactCd를 못 얻으면 detail 자체를 사용 (최소한 email 등은 표시 가능)
            detail = ctrlDetail;
          }
        } else {
          const res = await docGenApi.getContactDetail(entityCd.value, signer.id);
          detail = res?.data;
        }
      }
      } // end: else if (signer.id)
    } // end: if (!detail) — fallback chain

    if (!detail) return;

    const fields: DetailField[] = [];
    for (const [key, label] of DETAIL_FIELD_MAP) {
      const val = detail[key];
      if (val && String(val).trim()) {
        const strVal = String(val).trim();
        const isDate = DATE_FIELD_KEYS.has(key);
        const parsedDate = isDate ? parseDateFromFormat(strVal, userDateFormat.value) : undefined;
        fields.push({ key, label, value: strVal, parsedDate: parsedDate || undefined });
      }
    }

    // 상세 조회에서 email 보강 (목록에 없었을 경우)
    if (!signer.email && detail.contactEmail) {
      const signerIdx = dialogSelected.value.findIndex(s => s.id === signer.id);
      if (signerIdx >= 0) {
        dialogSelected.value[signerIdx] = { ...dialogSelected.value[signerIdx], email: detail.contactEmail };
      }
    }

    signerDetailCache.value = { ...signerDetailCache.value, [signer.id]: fields };
  } catch (err) {
    console.error('Failed to fetch signer detail:', err);
  } finally {
    signerDetailLoading.value = { ...signerDetailLoading.value, [signer.id]: false };
  }
}

function addDetailToExtraLine(signerIdx: number, field: DetailField) {
  const signer = dialogSelected.value[signerIdx];
  const lines = getSignerExtraLines(signer);
  const displayVal = getDetailFieldDisplay(field);
  const lineText = `${field.label}: ${displayVal}`;
  if (!lines.includes(lineText)) {
    lines.push(lineText);
    syncExtraText(signerIdx);
  }
}

function canFetchDetail(signer: SignerEntry): boolean {
  if (signer.isManual) return false;
  // officerIdx 또는 contactCd가 있으면 조회 가능
  if (signer.officerIdx != null) return true;
  if (signer.contactCd) return true;
  // fallback: id 값으로 시도 가능 (기존 저장된 데이터 호환)
  if (signer.id && signer.role && signer.role !== 'manual') return true;
  return false;
}

// ── 날짜 선택 (공통 캘린더) ──
const datePickerLabel = ref('');
const datePickerValue = ref('');

function datePickerPreview(): string {
  if (!datePickerValue.value) return '';
  const [y, m, d] = datePickerValue.value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const formatted = formatWithPattern(date, effectiveDateFormat.value);
  const label = datePickerLabel.value.trim();
  return label ? `${label}: ${formatted}` : formatted;
}

function addDateToSigner(signerIdx: number, menuClose?: () => void) {
  if (!datePickerValue.value) return;
  const lineText = datePickerPreview();
  if (!lineText) return;

  const signer = dialogSelected.value[signerIdx];
  const lines = getSignerExtraLines(signer);
  if (!lines.includes(lineText)) {
    lines.push(lineText);
    syncExtraText(signerIdx);
  }
  datePickerValue.value = '';
  if (menuClose) menuClose();
}

function addDateToAllSigners(menuClose?: () => void) {
  if (!datePickerValue.value) return;
  const lineText = datePickerPreview();
  if (!lineText) return;

  dialogSelected.value.forEach((signer, idx) => {
    const lines = getSignerExtraLines(signer);
    if (!lines.includes(lineText)) {
      lines.push(lineText);
      syncExtraText(idx);
    }
  });
  datePickerValue.value = '';
  if (menuClose) menuClose();
}

// ── 날짜 포맷 변경 시 이미 입력된 extra line 자동 업데이트 ──
watch(detailDateFormat, (newFmt) => {
  if (!newFmt) return;

  dialogSelected.value.forEach((signer, idx) => {
    const cachedFields = signerDetailCache.value[signer.id];
    if (!cachedFields) return;

    const lines = getSignerExtraLines(signer);
    let changed = false;

    for (const field of cachedFields) {
      if (!field.parsedDate) continue; // 날짜 필드만 처리
      const prefix = `${field.label}: `;
      const lineIdx = lines.findIndex((l) => l.startsWith(prefix));
      if (lineIdx >= 0) {
        lines[lineIdx] = `${prefix}${formatWithPattern(field.parsedDate, newFmt)}`;
        changed = true;
      }
    }

    if (changed) syncExtraText(idx);
  });
});

// ── 적용된 extra line 요약 (Batch 모드) ──
const appliedExtraLineLabels = computed(() => {
  const labelMap = new Map<string, number>();
  for (const signer of dialogSelected.value) {
    const lines = getSignerExtraLines(signer);
    for (const line of lines) {
      if (!line.trim()) continue;
      labelMap.set(line, (labelMap.get(line) || 0) + 1);
    }
  }
  // 같은 라벨(: 앞부분)끼리 그룹핑
  const grouped = new Map<string, { label: string; count: number }>();
  for (const [line, count] of labelMap) {
    const colonIdx = line.indexOf(': ');
    const label = colonIdx >= 0 ? line.substring(0, colonIdx) : line;
    if (grouped.has(label)) {
      grouped.get(label)!.count += count;
    } else {
      grouped.set(label, { label, count });
    }
  }
  return Array.from(grouped.values());
});

function removeExtraLineFromAll(label: string) {
  const prefix = label + ': ';
  dialogSelected.value.forEach((signer, idx) => {
    const lines = getSignerExtraLines(signer);
    const filtered = lines.filter((l) => l !== label && !l.startsWith(prefix));
    extraLinesMap.value[signer.id] = filtered;
    syncExtraText(idx);
  });

  // 디테일 필드 추적에서 제거
  appliedDetailFieldKeys.value = appliedDetailFieldKeys.value.filter((f) => f.label !== label);
}

function clearAllExtraLines() {
  dialogSelected.value.forEach((signer, idx) => {
    extraLinesMap.value[signer.id] = [];
    syncExtraText(idx);
  });
  // 디테일 필드 추적 초기화
  appliedDetailFieldKeys.value = [];
}

// ── Batch detail fetch ──
const batchDetailLoading = ref(false);
const batchDetailFetched = ref(false);

/** 전체 서명자 상세정보 일괄 조회 */
async function fetchAllSignerDetails() {
  const fetchable = dialogSelected.value.filter((s) => canFetchDetail(s) && !signerDetailCache.value[s.id]);
  if (fetchable.length === 0) {
    batchDetailFetched.value = true;
    return;
  }
  batchDetailLoading.value = true;
  try {
    await Promise.allSettled(fetchable.map((s) => fetchSignerDetail(s)));
    batchDetailFetched.value = true;
  } finally {
    batchDetailLoading.value = false;
  }
}

/** 전체 서명자에서 사용 가능한 필드 타입 목록 (하나 이상의 서명자가 값을 가진 필드만) */
const availableBatchFields = computed(() => {
  const result: { key: string; label: string }[] = [];
  for (const [key, label] of DETAIL_FIELD_MAP) {
    const hasAny = dialogSelected.value.some((s) => {
      const fields = signerDetailCache.value[s.id];
      return fields?.some((f) => f.key === key);
    });
    if (hasAny) result.push({ key, label });
  }
  return result;
});

/** 필드 타입 클릭 → 전체 서명자에 각자의 값으로 extra line 추가 */
function addDetailFieldToAll(fieldKey: string, fieldLabel: string) {
  // 디테일 필드 키 추적 (새 사이너 자동 적용용)
  if (!appliedDetailFieldKeys.value.some((f) => f.key === fieldKey)) {
    appliedDetailFieldKeys.value.push({ key: fieldKey, label: fieldLabel });
  }

  dialogSelected.value.forEach((signer, idx) => {
    const fields = signerDetailCache.value[signer.id];
    const field = fields?.find((f) => f.key === fieldKey);
    if (field) {
      const lines = getSignerExtraLines(signer);
      const displayVal = getDetailFieldDisplay(field);
      const lineText = `${fieldLabel}: ${displayVal}`;
      if (!lines.includes(lineText)) {
        lines.push(lineText);
        syncExtraText(idx);
      }
    }
  });
}

// ── Preview helpers ──
const SIGNERS_PER_ROW = 2;

const previewRows = computed(() => {
  const rows: SignerEntry[][] = [];
  for (let i = 0; i < dialogSelected.value.length; i += SIGNERS_PER_ROW) {
    rows.push(dialogSelected.value.slice(i, i + SIGNERS_PER_ROW));
  }
  return rows;
});

function getDisplayName(signer: SignerEntry): string {
  const titleVisible = signer.showTitle !== false;
  const effectiveTitle = titleVisible ? (signer.customTitle || signer.title || '') : '';
  return effectiveTitle ? `${signer.name} (${effectiveTitle})` : signer.name;
}

function getExtraLines(signer: SignerEntry): string[] {
  if (!signer.extraText) return [];
  return signer.extraText.split(/\r?\n/).filter((l) => l.trim());
}

function toPersistedSigner(signer: SignerEntry): SignerEntry {
  const { _signerLayout: _layout, ...rest } = signer;
  void _layout;
  return rest;
}

function confirmSelection() {
  emit('update', JSON.stringify(dialogSelected.value.map(toPersistedSigner)));
  dialogOpen.value = false;
}

async function loadOfficers() {
  if (!entityCd.value) return;
  loading.value = true;
  roleOfficers.value = [];

  const roleKey = selectedRoleIndex.value;
  try {
    let res: any;
    switch (roleKey) {
      case 'DIRECTOR':
        res = await docGenApi.getDirectors(entityCd.value);
        roleOfficers.value = (res?.data?.directors || []).map((d: any) => ({
          id: String(d.id || d.officerIdx || d.contactCd || ''),
          name: d.contactName || d.name || '',
          title: d.positionName || 'Director',
          role: 'director',
          email: d.email || d.contactEmail || '',
          officerIdx: d.officerIdx != null ? Number(d.officerIdx) : undefined,
          contactCd: d.contactCd || undefined,
        }));
        break;
      case 'SECRETARY':
        res = await docGenApi.getSecretaries(entityCd.value);
        roleOfficers.value = (res?.data?.secretaries || []).map((d: any) => ({
          id: String(d.id || d.officerIdx || d.contactCd || ''),
          name: d.contactName || d.name || '',
          title: d.positionName || 'Secretary',
          role: 'secretary',
          email: d.email || d.contactEmail || '',
          officerIdx: d.officerIdx != null ? Number(d.officerIdx) : undefined,
          contactCd: d.contactCd || undefined,
        }));
        break;
      case 'MANAGER':
        res = await docGenApi.getManagers(entityCd.value);
        roleOfficers.value = (res?.data?.managers || []).map((d: any) => ({
          id: String(d.id || d.officerIdx || d.contactCd || ''),
          name: d.contactName || d.name || '',
          title: d.positionName || 'Manager',
          role: 'manager',
          email: d.email || d.contactEmail || '',
          officerIdx: d.officerIdx != null ? Number(d.officerIdx) : undefined,
          contactCd: d.contactCd || undefined,
        }));
        break;
      case 'SHAREHOLDER':
        // jurisdiction 별 분기 — MY 는 entity_my/members (정확한 dedup)
        res = await docGenApi.getMembers(entityCd.value);
        roleOfficers.value = (res?.data?.members || res?.data?.shares || []).map((s: any) => ({
          id: String(s.shareholderIdx || s.shareCapitalIdx || s.contactCd || ''),
          name: s.contactName || s.shareholderName || s.name || '',
          title: isJurisdictionMY.value ? 'Member' : 'Shareholder',
          role: 'shareholder',
          email: s.email || s.contactEmail || '',
          contactCd: s.contactCd || undefined,
        }));
        break;
      case 'CONTROLLER':
        // MY: getBeneficialOwners → /entity_my/beneficial-owner (L_BENEFICIALOWNER)
        // SG: getControllers → /entity/controller
        res = isJurisdictionMY.value
          ? await docGenApi.getBeneficialOwners(entityCd.value)
          : await docGenApi.getControllers(entityCd.value);
        const controllerList = res?.data?.beneficialOwners
          || (Array.isArray(res?.data) ? res.data : (res?.data?.controllers || []));
        roleOfficers.value = (controllerList || []).map((c: any) => {
          const id = String(
            c.id ??
            c.ctrlIdx ??
            c.ctrlDetailIdx ??
            c.ctrlListIdx ??
            c.officerIdx ??
            c.contactCd ??
            `${c.name || c.contactName || 'controller'}-${Math.random().toString(36).slice(2, 9)}`
          );

          // ⭐ MY BO 는 list 응답에 detail 정보 (contactRegNo, primaryAddress, contactEmail, mobileNo, dateOfBirth, ...) 가
          //    이미 포함돼 있어, signerDetailCache 에 즉시 채워서 fetchSignerDetail 의 별도 호출 우회.
          //    (BO 는 L_OFFICER 가 아니라 L_BENEFICIALOWNER → getOfficerDetail 이 안 통함)
          if (isJurisdictionMY.value) {
            const fields: DetailField[] = [];
            for (const [key, label] of DETAIL_FIELD_MAP) {
              const val = c[key];
              if (val && String(val).trim()) {
                const strVal = String(val).trim();
                const isDate = DATE_FIELD_KEYS.has(key);
                const parsedDate = isDate
                  ? parseDateFromFormat(strVal, userDateFormat.value)
                  : undefined;
                fields.push({ key, label, value: strVal, parsedDate: parsedDate || undefined });
              }
            }
            if (fields.length > 0) {
              signerDetailCache.value = { ...signerDetailCache.value, [id]: fields };
            }
          }

          return {
            id,
            name: c.contactName || c.name || '',
            title: isJurisdictionMY.value ? 'Beneficial Owner' : (c.positionName || 'Controller'),
            role: 'controller',
            email: c.email || c.contactEmail || '',
            officerIdx: c.officerIdx != null ? Number(c.officerIdx) : undefined,
            contactCd: c.contactCd || undefined,
          };
        });
        break;
      case 'CONTACT':
        res = await docGenApi.getContacts(entityCd.value);
        roleOfficers.value = (res?.data?.contacts || []).map((c: any) => ({
          id: String(c.contactCd || ''),
          name: c.contactName || c.name || '',
          title: c.positionName || 'Contact',
          role: 'contact',
          email: c.email || c.contactEmail || '',
          contactCd: c.contactCd || undefined,
        }));
        break;
    }
  } catch (err) {
    console.error(`Failed to load ${roleKey}:`, err);
    roleOfficers.value = [];
  } finally {
    loading.value = false;
  }
}

watch(selectedRoleIndex, () => {
  if (dialogOpen.value) {
    officerSearchText.value = ''; // 탭 전환 시 검색어 초기화
    loadOfficers();
  }
});

// ── Actions ──
function removeSigner(index: number) {
  const target = displaySigners.value[index];
  if (!target) return;
  const fullIdx = signers.value.findIndex((s) => s.id === target.id);
  if (fullIdx < 0) return;
  const updated = [...signers.value];
  updated.splice(fullIdx, 1);
  emit('update', JSON.stringify(updated));
}

function clearAll() {
  emit('update', JSON.stringify([]));
}

function addManualSigner() {
  const name = manualName.value.trim();
  if (!name) return;

  const entry: SignerEntry = {
    id: crypto.randomUUID(),
    name,
    title: '',
    role: 'manual',
    isManual: true,
    showTitle: false,
    customTitle: '',
  };

  const updated = [...signers.value, entry];
  emit('update', JSON.stringify(updated));
  manualName.value = '';
}

defineExpose({ openDialog });
</script>

<style scoped>
.signer-list-field {
  width: 100%;
}
.signer-chips {
  display: flex;
  flex-wrap: wrap;
}
.officer-list {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

/* ── Batch Form ── */
.batch-form {
  background: #f0f4ff;
  border: 1px solid #d0d7ff;
  border-radius: 8px;
}

/* ── Signature Preview ── */
.signature-preview {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
}
.signature-preview-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 12px;
}
.signature-preview-table:last-child {
  margin-bottom: 0;
}
.signature-cell {
  padding: 8px 12px;
  vertical-align: top;
}
.signature-line-space {
  height: 40px;
}
.signature-line {
  font-size: 13px;
  color: #555;
}
.signer-display-name {
  font-size: 13px;
  font-weight: 500;
  margin-top: 4px;
}
.signer-extra-line {
  font-size: 12px;
  color: #666;
}

/* ── Individual: Signer name header ── */
.signer-name-header {
  background: #e8eef8;
  border-left: 3px solid #1976d2;
}

/* ── Detail Fields ── */
.detail-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

/* ── Skip ── */
.field-skipped {
  opacity: 0.5;
}

.skip-btn {
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.15s;
}
.skip-btn:hover {
  opacity: 1;
}

.restore-link {
  font-size: 12px;
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
  margin-left: 4px;
}
</style>
